import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import ListPageHeader from '../common/ListPageHeader.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import { Users } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import {
  formCardClass,
  formInputClass,
  formLabelClass,
  formSelectClass,
  formTextareaClass,
} from '../common/uiClasses.js';


const AddShipmentClient = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    contact: '',
    address: '',
    company_name: '',
    gst_number: '',
    status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    full_name:    (v) => validators.required(v, 'Full name'),
    company_name: (v) => validators.required(v, 'Company name'),
    email:        (v) => validators.required(v, 'Email') || validators.email(v),
    contact:      (v) => validators.required(v, 'Contact') || validators.contact(v),
    address:      (v) => validators.required(v, 'Address'),
    gst_number:   (v) => v ? validators.gst(v) : '',
  };

  const statusOptions = ['active', 'inactive'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === 'contact') filtered = inputFilters.digits10(value);
    else if (name === 'gst_number') filtered = value.toUpperCase();

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(formData, validationRules)) return;
    setLoading(true);

    try {
      await shipmentClientService.create(formData);
      Alert.success('Shipment client created successfully!');
      setTimeout(() => navigate('/shipment-clients'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create shipment client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <ListPageHeader
          title={t('pages.shipmentClient.add')}
          icon={Users}
          backTo="/shipment-clients"
          titleClassName="text-secondary"
          iconClassName="w-5 h-5 text-secondary shrink-0"
          className="mb-4 sm:mb-6"
        />

        <form onSubmit={handleSubmit} className={`${formCardClass} space-y-6 sm:space-y-8`}>
          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
              <div>
                <label className={formLabelClass}>
                  Full Name <span className="text-danger font-bold">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className={formInputClass(!!errors.full_name)}
                  required
                />
                <ValidationMessage error={errors.full_name} />
              </div>

              <div>
                <label className={formLabelClass}>
                  Company Name <span className="text-danger font-bold">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  className={formInputClass(!!errors.company_name)}
                  required
                />
                <ValidationMessage error={errors.company_name} />
              </div>

              <div className="sm:col-span-2 md:col-span-1">
                <label className={formLabelClass}>GST Number</label>
                <input
                  type="text"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={handleChange}
                  placeholder="Enter GST number (optional)"
                  maxLength="15"
                  className={formInputClass(!!errors.gst_number)}
                />
                <ValidationMessage error={errors.gst_number} />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
              <div>
                <label className={formLabelClass}>
                  Email <span className="text-danger font-bold">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className={formInputClass(!!errors.email)}
                  required
                />
                <ValidationMessage error={errors.email} />
              </div>

              <div>
                <label className={formLabelClass}>
                  Contact Number <span className="text-danger font-bold">*</span>
                </label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  maxLength={10}
                  className={formInputClass(!!errors.contact)}
                  required
                />
                <ValidationMessage error={errors.contact} />
              </div>

              <div className="sm:col-span-2">
                <label className={formLabelClass}>
                  Address <span className="text-danger font-bold">*</span>
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter complete address"
                  rows="3"
                  className={formTextareaClass(!!errors.address)}
                  required
                />
                <ValidationMessage error={errors.address} />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Status
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
              <div>
                <label className={formLabelClass}>Status</label>
                <SelectField
                  variant="form"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={formSelectClass()}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.toUpperCase()}
                    </option>
                  ))}
                </SelectField>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4 border-t border-gray-100">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate('/shipment-clients')}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AddShipmentClient;
